import React, { useEffect, useState } from "react";
import { StartScreen } from "./StartScreen";
import { GameScreen } from "./GameScreen";
import {
  clearActivityHistory,
  clearCharacterHistory,
  getActivityHistory,
  getCharacterHistory,
  saveActivityHistory,
  saveCharacterHistory,
} from "./history";
import { Locale, locales } from "./data";
import { trackEvent } from "./trackEvent";

export const App: React.FC = () => {
  //
  // State
  //

  const [locale, setLocale] = useState<Locale>(locales.en);

  const [selectedCategoryNames, setSelectedCategoryNames] = useState<string[]>(
    []
  );
  useEffect(() => {
    setSelectedCategoryNames(
      locale.categories.map((category) => category.name)
    );
  }, [locale]);

  const [mode, setMode] = useState<"character" | "activity" | "both">("both");

  const [item, setItem] = useState<{
    character?: string;
    category?: string;
    activity?: string;
  } | null>(null);

  const goToNextItem = () => {
    //
    // Character
    //

    const characterHistory = getCharacterHistory();

    const getRandomCharacter = (): {
      character: string;
      category: string;
    } | null => {
      for (let i = 0; i < 500; i++) {
        const selectedCategories = locale.categories.filter((category) =>
          selectedCategoryNames.includes(category.name)
        );

        const randomCategory =
          selectedCategories[
            Math.floor(Math.random() * selectedCategories.length)
          ];

        const randomCharacter =
          randomCategory.characters[
            Math.floor(Math.random() * randomCategory.characters.length)
          ];

        if (
          !characterHistory.includes(randomCharacter) &&
          randomCharacter !== "TODO"
        ) {
          return {
            character: randomCharacter,
            category: randomCategory.name,
          };
        }
      }

      return null;
    };

    let character: ReturnType<typeof getRandomCharacter> | undefined;

    if (mode === "character" || mode === "both") {
      character =
        getRandomCharacter() ||
        (() => {
          clearCharacterHistory();
          return getRandomCharacter();
        })();

      if (!character) {
        return;
      }
    }

    //
    // Activity
    //

    const activityHistory = getActivityHistory();

    const getRandomActivity = (): string | undefined => {
      for (let i = 0; i < 500; i++) {
        const randomActivity =
          locale.activities[
            Math.floor(Math.random() * locale.activities.length)
          ];

        if (!activityHistory.includes(randomActivity)) {
          return randomActivity;
        }
      }

      return undefined;
    };

    let activity: string | undefined;

    if (mode === "activity" || mode === "both") {
      activity =
        getRandomActivity() ||
        (() => {
          clearActivityHistory();
          return getRandomActivity();
        })();

      if (!activity) {
        return;
      }
    }

    //
    // Done
    //

    if (character) {
      saveCharacterHistory([...characterHistory, character.character]);
    }

    if (activity) {
      saveActivityHistory([...activityHistory, activity]);
    }

    setItem({
      character: character?.character,
      category: character?.category,
      activity,
    });
  };

  //
  // UI
  //

  if (item) {
    return (
      <GameScreen
        locale={locale}
        character={item.character}
        category={item.category}
        activity={item.activity}
        onNext={() => {
          goToNextItem();
          trackEvent("Clicked Next");
        }}
        onBack={() => {
          setItem(null);
          trackEvent("Clicked Home");
        }}
      />
    );
  }

  if (!item) {
    return (
      <StartScreen
        locale={locale}
        onChangeLocale={(locale) => {
          setLocale(locale);
          trackEvent(`Changed locale: ${locale.localeCode}`);
        }}
        mode={mode}
        onChangeMode={(mode) => {
          setMode(mode);
          trackEvent(`Changed mode: ${mode}`);
        }}
        selectedCategoryNames={selectedCategoryNames}
        onToggleCategory={(categoryName) => {
          setSelectedCategoryNames((prev) => {
            if (prev.includes(categoryName)) {
              const newArr = prev.filter((item) => item !== categoryName);
              if (newArr.length === 0) {
                return [...prev];
              }
              trackEvent(`Unselected category: ${categoryName}`);
              return newArr;
            }

            trackEvent(`Selected category: ${categoryName}`);
            return [...prev, categoryName];
          });
        }}
        onStart={() => {
          goToNextItem();
          trackEvent("Clicked Start");
        }}
      />
    );
  }

  return null;
};
